import React, { useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  Col,
  Divider,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Radio,
  Row,
  Select,
  Steps,
  Typography,
  message,
} from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { enableNurseCareItemApi } from "../../../../services/nurse.services";
import SOS_CARE_GROUPS, {
  getUnorderedGroups,
  isFieldVisible,
  splitEnablePayload,
} from "./sosCareItemFields";
import useIsMobile from "../../../../hooks/useIsMobile";
import btnStyles from "../../../../styles/Custom/Button.module.css";
import inputStyles from "../../../../styles/Custom/Input.module.css";
import { NURSE_IP_COMPACT_MODAL_WRAP } from "../../Doctor/InitialInspection/ClosePopup";

const STEPS = [{ title: "Care Item" }, { title: "Details" }];

/*
 * The details step scrolls, so a dropdown rendered into the body stayed where
 * it was opened while the field moved away underneath it. Anchoring it to the
 * scrolling area keeps the two together; the body is only the fallback for
 * when there is no scroller around the field.
 */
const selectPopupContainer = (trigger) =>
  trigger?.closest?.(".sos-care-detail-scroll") || document.body;

/* The same artwork the care item tabs use, so a care item looks the same
   wherever it is offered. A group without one simply shows its name. */
const groupIcon = (key) => {
  try {
    return require(`../../../../assets/careItemIcons/${key}.png`);
  } catch (err) {
    return null;
  }
};

const SosCareItemsModal = ({
  open,
  setOpen,
  formData,
  admissionId,
  getData,
}) => {
  const isMobile = useIsMobile();
  const [form] = Form.useForm();
  const [step, setStep] = useState(0);
  const [groupKeys, setGroupKeys] = useState([]);
  const [careTypes, setCareTypes] = useState([]);
  const [saving, setSaving] = useState(false);
  /* Watching the whole form keeps the "Others" inputs in step with the select
     they hang off, the same way the doctor's form reveals them. */
  const watched = Form.useWatch([], form);

  const groups = useMemo(() => getUnorderedGroups(formData), [formData]);
  const chosenGroups = useMemo(
    () => groups.filter(({ key }) => groupKeys.includes(key)),
    [groups, groupKeys]
  );
  /* A group holding one type needs no second choice - picking the group is
     picking the type. */
  const selectedItems = useMemo(
    () =>
      chosenGroups.flatMap((group) =>
        group.items.length === 1
          ? group.items
          : group.items.filter(({ careType }) => careTypes.includes(careType))
      ),
    [chosenGroups, careTypes]
  );

  const close = () => {
    setStep(0);
    setGroupKeys([]);
    setCareTypes([]);
    form.resetFields();
    setOpen(false);
  };

  const toggleGroup = (key, checked) =>
    setGroupKeys((prev) =>
      checked ? [...prev, key] : prev.filter((item) => item !== key)
    );

  const toggleCareType = (careType, checked) =>
    setCareTypes((prev) =>
      checked ? [...prev, careType] : prev.filter((item) => item !== careType)
    );

  const goNext = () => {
    if (!groupKeys.length) {
      message.error("Please select at least one care item.");
      return;
    }
    setStep(1);
  };

  const submit = async () => {
    if (!selectedItems.length) {
      message.error("Please select at least one type.");
      return;
    }

    let values = {};
    try {
      values = await form.validateFields();
    } catch (err) {
      /* The details step scrolls, so a field that failed can be well off
         screen - the form looked like it had simply ignored the button. */
      const firstError = err?.errorFields?.[0]?.name;
      if (firstError) {
        form.scrollToField(firstError, {
          behavior: "smooth",
          block: "center",
        });
      }
      return;
    }

    setSaving(true);
    const added = [];
    const failed = [];
    const declined = [];
    /* One request per care type - a failure on one should not take the rest
       down with it. */
    for (const item of selectedItems) {
      const { careType, label } = item;
      const { drugName, data, declined: answeredNo } = splitEnablePayload(
        item,
        values?.[careType]
      );

      /* Answered "No" - that care is not being given, so nothing is added
         and no chart is opened for it. */
      if (answeredNo) {
        declined.push(label);
        continue;
      }

      try {
        /* Enabling is the nurse's own route - it records the care item
           against the admission with the nurse as `advised_by`, rather than
           writing an order on the doctor's order sheet. */
        await enableNurseCareItemApi(admissionId, {
          care_item_type: careType,
          drug_name: drugName,
          data,
        });
        added.push(label);
      } catch (err) {
        failed.push(label);
      }
    }

    setSaving(false);

    if (added.length) {
      message.success(
        added.length === 1
          ? `${added[0]} added. Record the SOS against it.`
          : `${added.length} care items added. Record the SOS against them.`
      );
    }
    if (failed.length) {
      message.error(`Could not add: ${failed.join(", ")}`);
    }
    if (declined.length) {
      message.info(
        `${declined.join(", ")} answered No, so ${
          declined.length === 1 ? "it was" : "they were"
        } not added.`
      );
    }
    if (!added.length) {
      if (declined.length && !failed.length) close();
      return;
    }

    close();
    await getData?.();
  };

  const renderField = (careType, field, itemLabel) => {
    const control =
      field.type === "select" ? (
        <Select
          options={field.options}
          className={inputStyles.mdSelect}
          placeholder={`Select ${field.label}`}
          getPopupContainer={selectPopupContainer}
          /* Shorter than the scrolling area it opens inside, so a field near
             the bottom still shows its list rather than having it cut off. */
          listHeight={160}
        />
      ) : field.type === "radio" ? (
        <Radio.Group options={field.options} />
      ) : field.type === "number" ? (
        <InputNumber
          addonAfter={field.addonAfter}
          controls={false}
          className={inputStyles.mdNumberInput}
          style={{ width: "100%" }}
        />
      ) : (
        <Input className={inputStyles.mdInput} />
      );

    /* A single-choice care item names its field the same as itself ("Eye
       Drops" under "Eye Drops"), which read as the heading twice. The row is
       already labelled by the item, so the field goes without. */
    const duplicateLabel = field.label === itemLabel;

    /* Comments is free text and reads as an afterthought squeezed into half a
       row next to a select - it gets the full width. */
    const fullWidth = field.name === "notes" || field.type === "text";

    return (
      <Col xs={24} md={fullWidth ? 24 : 12} key={field.name}>
        <Form.Item
          name={[careType, field.name]}
          label={duplicateLabel ? null : field.label}
          rules={
            field.required
              ? [{ required: true, message: `Please enter ${field.label}` }]
              : undefined
          }
        >
          {control}
        </Form.Item>
      </Col>
    );
  };

  const renderItemFields = (item) => (
    <Row gutter={isMobile ? [0, 0] : [16, 0]}>
      {item.fields
        .filter((field) => isFieldVisible(field, watched?.[item.careType]))
        .map((field) => renderField(item.careType, field, item.label))}
    </Row>
  );

  const title = "Record SOS Care Item";

  const footerButtons = (
    <div
      className="nurse-care-sos-modal-footer flex-end"
      style={{ gap: isMobile ? "10px" : "24px" }}
    >
      <Button
        type="primary"
        ghost={Boolean(groups.length)}
        className={btnStyles.mdBtn}
        disabled={saving}
        onClick={step === 1 ? () => setStep(0) : close}
      >
        {step === 1 ? "Back" : groups.length ? "Cancel" : "Close"}
      </Button>
      {groups.length > 0 && (
        <Button
          type="primary"
          className={btnStyles.mdBtn}
          loading={saving}
          onClick={step === 1 ? submit : goNext}
        >
          {step === 1 ? "Record SOS" : "Next"}
        </Button>
      )}
    </div>
  );

  return (
    <Modal
      title={
        isMobile ? (
          <span className="nurse-care-sos-modal-title">{title}</span>
        ) : (
          <Row>
            <Col span={12} className="flex-start">
              <Typography
                style={{
                  marginLeft: "12px",
                  fontWeight: "400",
                  fontSize: "20px",
                }}
              >
                {title}
              </Typography>
            </Col>
            <Col span={12} className="flex-end">
              <CloseOutlined onClick={close} />
            </Col>
            <Divider />
          </Row>
        )
      }
      closeIcon={isMobile ? <CloseOutlined onClick={close} /> : null}
      width={isMobile ? "min(380px, calc(100vw - 40px))" : "60%"}
      centered={isMobile}
      className={
        isMobile
          ? "nurse-care-sos-modal nurse-care-sos-modal-mobile"
          : "nurse-care-sos-modal"
      }
      wrapClassName={isMobile ? NURSE_IP_COMPACT_MODAL_WRAP : undefined}
      getContainer={() => document.body}
      open={open}
      destroyOnClose
      styles={
        isMobile
          ? {
              body: { paddingTop: 4, paddingBottom: 4 },
              footer: { paddingTop: 4 },
            }
          : undefined
      }
      footer={footerButtons}
      onCancel={close}
    >
      {groups.length > 0 && (
        <Steps
          size="small"
          current={step}
          items={STEPS}
          className="sos-care-steps"
        />
      )}

      {step === 0 ? (
        <>
          {groups.length ? (
            <>
            <Typography.Text type="secondary" className="sos-care-hint">
              These care items have not been ordered for this patient. Select
              the ones given as SOS.
            </Typography.Text>
            <Row
              gutter={isMobile ? [0, 10] : [16, 12]}
              className="sos-care-grid"
            >
              {groups.map((group) => {
                const checked = groupKeys.includes(group.key);
                const icon = groupIcon(group.key);
                return (
                  <Col xs={24} md={12} key={group.key}>
                    <div
                      className={`sos-care-option${
                        checked ? " sos-care-option--selected" : ""
                      }`}
                    >
                      <Checkbox
                        checked={checked}
                        onChange={(e) => toggleGroup(group.key, e.target.checked)}
                      >
                        <span className="sos-care-option-label">
                          {icon && (
                            <img
                              src={icon}
                              alt=""
                              className="sos-care-option-icon"
                            />
                          )}
                          <span>{group.label}</span>
                        </span>
                      </Checkbox>
                      {group.items.length > 1 && (
                        <span className="sos-care-option-count">
                          {group.items.length} types
                        </span>
                      )}
                    </div>
                  </Col>
                );
              })}
            </Row>
            </>
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Every care item has already been ordered for this patient"
            />
          )}
        </>
      ) : (
        <>
          <Typography.Text type="secondary" className="sos-care-hint">
            Choose the type and fill in its details, the same way it is
            ordered.
          </Typography.Text>

          <Form form={form} layout="vertical" className="sos-care-form">
            <div className="sos-care-detail-scroll">
              {chosenGroups.map((group) => (
                <div className="sos-care-section" key={group.key}>
                  <Typography.Text strong className="sos-care-section-title">
                    {groupIcon(group.key) && (
                      <img
                        src={groupIcon(group.key)}
                        alt=""
                        className="sos-care-option-icon"
                      />
                    )}
                    {group.label}
                  </Typography.Text>

                  {group.items.length === 1
                    ? renderItemFields(group.items[0])
                    : group.items.map((item) => {
                        const checked = careTypes.includes(item.careType);
                        return (
                          <div className="sos-care-subitem" key={item.careType}>
                            <Checkbox
                              checked={checked}
                              onChange={(e) =>
                                toggleCareType(item.careType, e.target.checked)
                              }
                            >
                              {item.label}
                            </Checkbox>
                            {checked && item.fields.length > 0 && (
                              <div className="sos-care-subitem-fields">
                                {renderItemFields(item)}
                              </div>
                            )}
                          </div>
                        );
                      })}
                </div>
              ))}
            </div>
          </Form>
        </>
      )}
      {!isMobile && <Divider />}
    </Modal>
  );
};

export default SosCareItemsModal;
export { SOS_CARE_GROUPS };
